import React from 'react';
import { RefreshCw } from 'lucide-react';
import { reloadToLatestApp, subscribeAppUpdatePrompt } from '../lib/startAppUpdateWatcher';
import { UI_Z_CLASS } from '../utils/uiZIndex';

const BANNER_ATTR = 'data-app-update-banner';
const BANNER_HEIGHT_VAR = '--app-update-banner-height';
/** h-9 button + py-2 — used until ResizeObserver measures the real height. */
const BANNER_FALLBACK_HEIGHT = '3.25rem';

function clearBannerLayout(): void {
  const root = document.documentElement;
  root.removeAttribute(BANNER_ATTR);
  root.style.removeProperty(BANNER_HEIGHT_VAR);
}

function applyBannerHeight(px: number | string): void {
  const root = document.documentElement;
  root.setAttribute(BANNER_ATTR, '');
  root.style.setProperty(BANNER_HEIGHT_VAR, typeof px === 'number' ? `${px}px` : px);
}

function observeBannerHeight(el: HTMLElement): () => void {
  const applyHeight = () => applyBannerHeight(el.getBoundingClientRect().height);
  applyHeight();
  const observer = new ResizeObserver(applyHeight);
  observer.observe(el);
  return () => observer.disconnect();
}

export function AppUpdateBanner() {
  const [available, setAvailable] = React.useState(false);
  const stopObservingRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    return subscribeAppUpdatePrompt((next) => {
      if (next) applyBannerHeight(BANNER_FALLBACK_HEIGHT);
      else clearBannerLayout();
      setAvailable(next);
    });
  }, []);

  React.useEffect(() => {
    return () => {
      stopObservingRef.current?.();
      stopObservingRef.current = null;
      clearBannerLayout();
    };
  }, []);

  const setBannerNode = React.useCallback((node: HTMLDivElement | null) => {
    stopObservingRef.current?.();
    stopObservingRef.current = null;
    if (!node) {
      clearBannerLayout();
      return;
    }
    stopObservingRef.current = observeBannerHeight(node);
  }, []);

  if (!available) return null;

  return (
    <div
      ref={setBannerNode}
      className={`sticky top-0 ${UI_Z_CLASS.appUpdateBanner} flex shrink-0 items-center justify-center gap-3 bg-slate-900 px-4 py-2 text-sm text-white shadow-md`}
      role="status"
      aria-live="polite"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
      <span className="min-w-0">Neue Version verfügbar.</span>
      <button
        type="button"
        onClick={() => reloadToLatestApp({ force: true })}
        className="inline-flex h-9 shrink-0 items-center rounded-md bg-app-surface px-3.5 text-sm font-semibold text-app-text hover:bg-app-hover"
      >
        Aktualisieren
      </button>
    </div>
  );
}
